/**
 * Utility functions to parse RawData from Streamline/Guesty and extract property information
 * Updated to use backend API for image fetching
 */

import { getPropertyImageUrl as fetchPropertyImageUrl } from '../services/propertyImagesApi';

/**
 * Parse Streamline raw data to extract property images and details
 * @param {Object} rawData - Raw data from Streamline API
 * @returns {Object} Parsed property data
 */
export function parseStreamlineData(rawData) {
  if (!rawData || !rawData.raw) return null;

  const data = rawData.raw;
  
  return {
    // Property images from Streamline
    images: extractStreamlineImages(data),
    
    // Property details
    title: data.name || data.title || data.property_name,
    description: data.description || data.summary,
    bedrooms: data.bedrooms || data.bed_count,
    bathrooms: data.bathrooms || data.bath_count,
    guests: data.guests || data.max_guests || data.sleeps,
    
    // Amenities
    amenities: data.amenities || data.features || [],
    
    // Pricing
    basePrice: data.base_price || data.nightly_rate,
    cleaningFee: data.cleaning_fee,
    serviceFee: data.service_fee,
    
    // Location details
    neighborhood: data.neighborhood || data.area,
    propertyType: data.property_type || data.type,
    
    // Status
    status: data.status || data.availability_status,
    isActive: data.active !== false,
    
    // Streamline specific
    streamlineId: data.id || data.property_id,
    lastUpdated: data.updated_at || data.last_modified,
  };
}

/**
 * Parse Guesty raw data to extract property images and details
 * @param {Object} rawData - Raw data from Guesty API
 * @returns {Object} Parsed property data
 */
export function parseGuestyData(rawData) {
  if (!rawData || !rawData.raw) return null;

  const data = rawData.raw;
  
  return {
    // Property images from Guesty
    images: extractGuestyImages(data),
    
    // Property details
    title: data.title || data.name,
    description: data.description,
    bedrooms: data.bedrooms,
    bathrooms: data.bathrooms,
    guests: data.guests || data.maxGuests,
    
    // Amenities
    amenities: data.amenities || [],
    
    // Pricing
    basePrice: data.basePrice || data.price,
    cleaningFee: data.cleaningFee,
    serviceFee: data.serviceFee,
    
    // Location details
    neighborhood: data.neighborhood,
    propertyType: data.propertyType,
    
    // Status
    status: data.status,
    isActive: data.active !== false,
    
    // Guesty specific
    guestyId: data.id,
    lastUpdated: data.updatedAt,
  };
}

/**
 * Extract images from Streamline data
 * @param {Object} data - Streamline raw data
 * @returns {Array} Array of image objects
 */
function extractStreamlineImages(data) {
  const images = [];
  
  // Primary image path (as mentioned in requirements)
  if (data.default_image_path) {
    images.push({
      url: data.default_image_path,
      isPrimary: true,
      caption: data.default_image_caption || '',
    });
  }
  
  // Additional images
  if (data.images && Array.isArray(data.images)) {
    data.images.forEach((img, index) => {
      images.push({
        url: img.url || img.path || img.src,
        isPrimary: index === 0 && !data.default_image_path,
        caption: img.caption || img.description || '',
        alt: img.alt || data.title || 'Property image',
      });
    });
  }
  
  // Photos array
  if (data.photos && Array.isArray(data.photos)) {
    data.photos.forEach((photo, index) => {
      images.push({
        url: photo.url || photo.path || photo.src,
        isPrimary: index === 0 && !data.default_image_path && images.length === 0,
        caption: photo.caption || photo.description || '',
        alt: photo.alt || data.title || 'Property photo',
      });
    });
  }
  
  return images;
}

/**
 * Extract images from Guesty data
 * @param {Object} data - Guesty raw data
 * @returns {Array} Array of image objects
 */
function extractGuestyImages(data) {
  const images = [];
  
  // Guesty images structure
  if (data.images && Array.isArray(data.images)) {
    data.images.forEach((img, index) => {
      images.push({
        url: img.url || img.src,
        isPrimary: index === 0,
        caption: img.caption || img.description || '',
        alt: img.alt || data.title || 'Property image',
      });
    });
  }
  
  // Photos array
  if (data.photos && Array.isArray(data.photos)) {
    data.photos.forEach((photo, index) => {
      images.push({
        url: photo.url || photo.src,
        isPrimary: index === 0 && images.length === 0,
        caption: photo.caption || photo.description || '',
        alt: photo.alt || data.title || 'Property photo',
      });
    });
  }
  
  return images;
}

/**
 * Get property image URL using backend API
 * @param {Object} property - Property object
 * @param {string} hostId - Host ID
 * @returns {Promise<String>} Image URL or null
 */
export async function getPropertyImageUrlFromAPI(property, hostId) {
  try {
    const propertyId = property.streamlinePropertyId || property.guestyPropertyId;
    if (!propertyId || !hostId) {
      console.log('Missing propertyId or hostId:', { propertyId, hostId });
      return null;
    }

    console.log('Fetching image from API for property:', {
      propertyId,
      hostId,
      propertySource: property.propertySource
    });

    const imageUrl = await fetchPropertyImageUrl(hostId, propertyId, property.propertySource);
    console.log('API returned image URL:', imageUrl);
    
    return imageUrl;
  } catch (error) {
    console.error('Error fetching property image from API:', error);
    return null;
  }
}

/**
 * Legacy function for backward compatibility - now uses API
 * @param {Object} property - Property object
 * @param {Array} rawDataList - Array of raw data objects (deprecated)
 * @returns {String} Image URL or fallback
 */
export function getPropertyImageUrl(property, rawDataList = []) {
  console.warn('getPropertyImageUrl is deprecated. Use getPropertyImageUrlFromAPI instead.');
  
  // For backward compatibility, return null to use default gradient
  // The Property component should be updated to use the API version
  return null;
}

/**
 * Get property details from raw data
 * @param {Object} property - Property object
 * @param {Array} rawDataList - Array of raw data objects
 * @returns {Object} Enhanced property data
 */
export function getEnhancedPropertyData(property, rawDataList = []) {
  // Find matching raw data by property ID
  const rawData = rawDataList.find(rd => {
    if (property.propertySource === 'streamline' && property.streamlinePropertyId) {
      // Check if raw is an array and find matching property by id
      if (Array.isArray(rd.raw)) {
        return rd.source === 'streamline' && rd.raw.some(item => item.id == property.streamlinePropertyId);
      }
      // Fallback for direct raw object
      return rd.source === 'streamline' && rd.raw?.id == property.streamlinePropertyId;
    }
    if (property.propertySource === 'guesty' && property.guestyPropertyId) {
      return rd.source === 'guesty' && rd.raw?.legacyId === property.guestyPropertyId;
    }
    return false;
  });
  
  if (!rawData) {
    return {
      ...property,
      imageUrl: null, // Will use default gradient
      pmsData: null,
    };
  }
  
  let parsedData = null;
  let imageUrl = null;
  
  if (rawData.source === 'streamline') {
    // Handle raw as array (new structure)
    if (Array.isArray(rawData.raw)) {
      const propertyData = rawData.raw.find(item => item.id == property.streamlinePropertyId);
      if (propertyData) {
        parsedData = {
          title: propertyData.name || propertyData.web_name,
          description: propertyData.description || propertyData.short_description,
          bedrooms: propertyData.bedrooms_number,
          bathrooms: propertyData.bathrooms_number,
          guests: propertyData.max_adults || propertyData.max_occupants,
          address: propertyData.address,
          city: propertyData.city,
          state: propertyData.state_name,
          zip: propertyData.zip,
          neighborhood: propertyData.location_area_name,
          propertyType: propertyData.home_type,
          rating: propertyData.rating_average,
          ratingCount: propertyData.rating_count,
        };
        
        // Try to find image URL using same logic as getPropertyImageUrl
        const imageFields = [
          'default_image_path',
          'default_thumbnail_path', 
          'image_path',
          'thumbnail_path',
          'location_image_path',
          'gallery_image_path'
        ];
        
        for (const field of imageFields) {
          if (propertyData[field]) {
            imageUrl = propertyData[field];
            break;
          }
        }
        
        // If no direct image path, try to construct one
        if (!imageUrl && propertyData.images_number > 0) {
          // Try multiple URL patterns for Streamline images
          const urlPatterns = [
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/default.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/1.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/main.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/primary.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/thumbnail.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/cover.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/hero.jpg`,
            `https://gallery.streamlinevrs.com/stl-default-images/${propertyData.company_id}/home/${propertyData.location_id}/featured.jpg`
          ];
          
          // Use the first pattern for now
          imageUrl = urlPatterns[0];
        }
      }
    }
    // Handle raw as object (legacy structure)
    else {
      parsedData = parseStreamlineData(rawData);
      imageUrl = rawData.raw?.default_image_path;
    }
  } else if (rawData.source === 'guesty') {
    parsedData = parseGuestyData(rawData);
    imageUrl = rawData.raw?.pictures?.[0]?.original;
  }
  
  return {
    ...property,
    imageUrl: imageUrl,
    pmsData: parsedData,
    // Override with PMS data if available
    title: parsedData?.title || property.listingTitle,
    description: parsedData?.description || property.description,
    bedrooms: parsedData?.bedrooms,
    bathrooms: parsedData?.bathrooms,
    guests: parsedData?.guests,
    amenities: parsedData?.amenities || [],
    basePrice: parsedData?.basePrice,
    propertyType: parsedData?.propertyType,
    neighborhood: parsedData?.neighborhood,
  };
}

/**
 * Calculate property statistics
 * @param {Array} properties - Array of properties
 * @param {Array} rawDataList - Array of raw data objects
 * @returns {Object} Property statistics
 */
export function calculatePropertyStats(properties, rawDataList = []) {
  const totalProperties = properties.length;
  const activeProperties = properties.filter(p => !p.isDeleted).length;
  const streamlineProperties = properties.filter(p => p.propertySource === 'streamline').length;
  const guestyProperties = properties.filter(p => p.propertySource === 'guesty').length;
  const hospitableProperties = properties.filter(p => p.propertySource === 'hospitable').length;
  const trackProperties = properties.filter(p => p.propertySource === 'track').length;
  const manualProperties = properties.filter(p => p.propertySource === 'manual').length;
  const pmsConnectedProperties = properties.filter(
    (p) => p.propertySource && p.propertySource !== 'manual'
  ).length;
  const providerBreakdown = [
    streamlineProperties ? `${streamlineProperties} Streamline` : null,
    guestyProperties ? `${guestyProperties} Guesty` : null,
    hospitableProperties ? `${hospitableProperties} Hospitable` : null,
    trackProperties ? `${trackProperties} Track` : null,
  ].filter(Boolean);
  
  return {
    totalProperties,
    activeProperties,
    streamlineProperties,
    guestyProperties,
    hospitableProperties,
    trackProperties,
    manualProperties,
    pmsConnectedProperties,
    providerBreakdown,
    pmsIntegrationRate: totalProperties > 0 ? Math.round((pmsConnectedProperties / totalProperties) * 100) : 0,
  };
}
